/**
 * Co-pilot PiP (mobile UX program, M5 — behind ui.mobileV2).
 *
 * The minimized form of an ACTIVE voice session: the page is the hero,
 * the agent rides along. Mobile finals frame 6:
 *   - 96–120px draggable PiP, edge-snap, citron live dot
 *   - the live face renders via the avatar-stage 'pip' dock — the SAME
 *     persistent <video> (liveReady survives the move, invariant I3)
 *   - mic chip straddling the corner; NO End control on the face
 *     (invariant I4) — tap the PiP to restore the sheet
 *   - no caption: it duplicated speech you can already hear, and the mic
 *     chip (which straddles the corner) cut into it (David 2026-10-25)
 *   - mode C (no live avatar) degrades to a compact audio pill
 */

import type { Signal } from '@preact/signals';
import { useEffect, useRef } from 'preact/hooks';
import { SoundBars } from './sound-bars';

export interface CopilotPipBits {
  /** True while the PiP should be visible (sessionMinimized && voice active). */
  visible: Signal<boolean>;
  /** Live face available (mode D, stream bound) — false → audio pill (mode C). */
  hasLiveVideo: Signal<boolean>;
  micMuted: Signal<boolean>;
  agentName: Signal<string>;
}

export interface CopilotPipHandlers {
  /** Tap the face/pill → restore the conversation surface. */
  onRestore: () => void;
  onToggleMute: () => void;
  /** Register the avatar-stage 'pip' dock while visible. */
  dock: { register(anchor: HTMLElement): void; release(): void };
}

const MIC_ON =
  '<path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/>';
const MIC_OFF =
  '<line x1="2" y1="2" x2="22" y2="22"/><path d="M18.89 13.23A7 7 0 0 0 19 12v-2"/><path d="M5 10v2a7 7 0 0 0 12 5"/><path d="M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12"/><line x1="12" y1="19" x2="12" y2="23"/>';

export function CopilotPip({
  bits,
  handlers,
}: {
  bits: CopilotPipBits;
  handlers: CopilotPipHandlers;
}) {
  const visible = bits.visible.value;
  const hasLive = bits.hasLiveVideo.value;
  const rootRef = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{ startX: number; startY: number; x: number; y: number; moved: boolean } | null>(null);

  // Dock the persistent live face while visible + mode D.
  useEffect(() => {
    if (!visible || !hasLive) return;
    const anchor = dockRef.current;
    if (!anchor) return;
    handlers.dock.register(anchor);
    return () => handlers.dock.release();
  }, [visible, hasLive]);

  // Drag + edge-snap. Pointer events on the container; a sub-4px move
  // counts as a tap (restore). Snaps to the nearer horizontal edge.
  useEffect(() => {
    const node = rootRef.current;
    if (!node || !visible) return;
    const onDown = (e: PointerEvent): void => {
      dragState.current = { startX: e.clientX, startY: e.clientY, x: e.clientX, y: e.clientY, moved: false };
      node.setPointerCapture(e.pointerId);
    };
    const onMove = (e: PointerEvent): void => {
      const d = dragState.current;
      if (!d) return;
      d.x = e.clientX;
      d.y = e.clientY;
      if (Math.abs(d.x - d.startX) + Math.abs(d.y - d.startY) > 4) d.moved = true;
      if (d.moved) {
        node.style.transition = 'none';
        node.style.left = `${e.clientX - node.offsetWidth / 2}px`;
        node.style.top = `${e.clientY - node.offsetHeight / 2}px`;
        node.style.right = 'auto';
        node.style.bottom = 'auto';
      }
    };
    const onUp = (e: PointerEvent): void => {
      const d = dragState.current;
      dragState.current = null;
      if (!d) return;
      if (!d.moved) {
        handlers.onRestore();
        return;
      }
      // Edge snap: nearer horizontal edge, clamped vertically to viewport.
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const snapLeft = e.clientX < vw / 2;
      const top = Math.min(Math.max(e.clientY - node.offsetHeight / 2, 12), vh - node.offsetHeight - 12);
      node.style.transition = 'left .18s ease, right .18s ease, top .18s ease';
      node.style.top = `${top}px`;
      if (snapLeft) {
        node.style.left = '12px';
        node.style.right = 'auto';
      } else {
        node.style.left = 'auto';
        node.style.right = '12px';
      }
    };
    node.addEventListener('pointerdown', onDown);
    node.addEventListener('pointermove', onMove);
    node.addEventListener('pointerup', onUp);
    return () => {
      node.removeEventListener('pointerdown', onDown);
      node.removeEventListener('pointermove', onMove);
      node.removeEventListener('pointerup', onUp);
    };
  }, [visible]);

  if (!visible) return null;

  const micMuted = bits.micMuted.value;

  return (
    <div
      ref={rootRef}
      class={`sp-pip${hasLive ? '' : ' sp-pip--audio'}`}
      role="button"
      aria-label="Return to the conversation"
    >
      {hasLive ? (
        <div class="sp-pip-face">
          {/* The avatar-stage 'pip' dock — the persistent live <video>. */}
          <div ref={dockRef} class="sp-pip-dock" />
          <span class="sp-pip-live-dot" aria-hidden="true" />
        </div>
      ) : (
        <div class="sp-pip-audio">
          <SoundBars recording height="14px" barWidth="2.5px" gap="2px" />
          <span class="sp-pip-audio-name">{bits.agentName.value}</span>
        </div>
      )}
      <button
        type="button"
        class={`sp-pip-mic${micMuted ? ' sp-pip-mic--muted' : ''}`}
        aria-label={micMuted ? 'Unmute microphone' : 'Mute microphone'}
        onClick={(e) => {
          e.stopPropagation();
          handlers.onToggleMute();
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          dangerouslySetInnerHTML={{ __html: micMuted ? MIC_OFF : MIC_ON }}
        />
      </button>
    </div>
  );
}
